import type { QueryClient } from "@tanstack/react-query";

import { LABOR_OPEN_SESSION_QUERY_KEY } from "@/components/LaborActiveSessionProvider";
import { invalidateIntegratorLaborMenuQueries } from "@/lib/hubLaborActiveSession";
import { clearLaborDevShellLock } from "@/lib/labor/laborDevShellLock";
import { laborDomainFromSessionPage } from "@/lib/labor/laborDomainFromPage";
import { clearLaborDepartureSnapshot } from "@/lib/labor/laborDepartureSnapshot";
import {
  clearLaborSessionTombstone,
  tombstoneLaborEndedSession,
} from "@/lib/labor/laborEndedSessionTombstone";
import {
  endLaborSession,
  endMobileLaborSession,
  fetchLaborOpenSession,
  type LaborActiveSessionPayload,
} from "@/lib/labor/laborApi";
import { readOpenLaborSessionPayloadFromCache } from "@/lib/labor/laborOpenSessionCache";
import {
  clearLaborPendingClockOut,
  readLaborPendingClockOut,
  writeLaborPendingClockOut,
} from "@/lib/labor/laborPendingClockOutOutbox";
import { parseLaborSessionRow } from "@/lib/labor/parseLaborSession";
import { setLaborOwnerDeparting } from "@/lib/labor/laborOwnerDeparture";
import type { LaborDomain } from "@/lib/labor/laborTarget";

const END_SESSION_RETRY_ATTEMPTS = 3;
const END_SESSION_RETRY_BASE_MS = 80;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Clears cached open-session presence without notifying other tabs (avoids stale refetch races). */
export function optimisticallyClearOpenLaborSession(queryClient: QueryClient): void {
  queryClient.setQueryData<LaborActiveSessionPayload>([...LABOR_OPEN_SESSION_QUERY_KEY], (current) => {
    if (current === undefined) {
      return current;
    }
    return {
      ...current,
      session: null,
      active_on_other_page: undefined,
      active_on_other_source: undefined,
    };
  });
  clearLaborDevShellLock({ notifyOtherTabs: false });
}

export async function endKnownLaborServerSession(input: {
  sessionId: number;
  idleTimeout?: boolean;
  domain: LaborDomain;
  keepalive?: boolean;
}): Promise<void> {
  await endLaborSession({
    sessionId: input.sessionId,
    idleTimeout: input.idleTimeout ?? false,
    domain: input.domain,
    keepalive: input.keepalive,
  });
}

export async function endKnownLaborServerSessionWithRetries(input: {
  sessionId: number;
  domain: LaborDomain;
  keepalive?: boolean;
}): Promise<boolean> {
  for (let attempt = 0; attempt < END_SESSION_RETRY_ATTEMPTS; attempt++) {
    try {
      await endKnownLaborServerSession({
        sessionId: input.sessionId,
        domain: input.domain,
        keepalive: input.keepalive,
      });
      return true;
    } catch {
      if (attempt < END_SESSION_RETRY_ATTEMPTS - 1) {
        await sleep(END_SESSION_RETRY_BASE_MS * (attempt + 1));
      }
    }
  }
  return false;
}

async function finalizeLaborServerEnd(queryClient: QueryClient, sessionId: number): Promise<void> {
  clearLaborPendingClockOut();
  clearLaborSessionTombstone(sessionId);
  invalidateIntegratorLaborMenuQueries(queryClient);
  await queryClient.refetchQueries({ queryKey: [...LABOR_OPEN_SESSION_QUERY_KEY] });
}

/**
 * Background clock-out for navigation: POST with retries, then broadcast so other tabs refetch.
 * Caller must have already tombstoned and optimistically cleared presence.
 */
export async function flushLaborServerClockOut(queryClient: QueryClient, input: {
  sessionId: number;
  domain: LaborDomain;
  keepalive?: boolean;
}): Promise<void> {
  const ended = await endKnownLaborServerSessionWithRetries({
    sessionId: input.sessionId,
    domain: input.domain,
    keepalive: input.keepalive ?? true,
  });
  if (ended) {
    await finalizeLaborServerEnd(queryClient, input.sessionId);
  }
}

/**
 * Re-sends any unconfirmed clock-out left in sessionStorage (for example after a failed POST).
 */
export async function retryPendingLaborClockOut(queryClient: QueryClient): Promise<void> {
  const pending = readLaborPendingClockOut();
  if (pending === null) {
    return;
  }
  tombstoneLaborEndedSession(pending.sessionId);
  await flushLaborServerClockOut(queryClient, {
    sessionId: pending.sessionId,
    domain: pending.domain,
    keepalive: false,
  });
}

function openSessionRowFromCacheOrFetch(queryClient: QueryClient): Promise<ReturnType<typeof parseLaborSessionRow>> {
  const cached = readOpenLaborSessionPayloadFromCache(queryClient);
  const cachedRow = parseLaborSessionRow(cached?.session ?? null);
  if (cachedRow !== null && cachedRow.id > 0) {
    return Promise.resolve(cachedRow);
  }
  return fetchLaborOpenSession().then((payload) => parseLaborSessionRow(payload.session));
}

/**
 * Ends the user's open `labor.device_session` row when one exists. No-op when none or id <= 0.
 */
export async function endOpenLaborServerSessionIfAny(): Promise<boolean> {
  try {
    const payload = await fetchLaborOpenSession();
    const row = parseLaborSessionRow(payload.session);
    if (row === null || row.id <= 0) {
      return false;
    }
    await endLaborSession({
      sessionId: row.id,
      idleTimeout: false,
      domain: laborDomainFromSessionPage(row.page ?? ""),
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Best-effort server clock-out when the session id was not in the navigation cache
 * (for example React Query open-session lagged behind page-local clock-in).
 */
export function backgroundEndOpenLaborServerSessionIfAny(queryClient: QueryClient): void {
  void (async () => {
    try {
      await endOpenLaborServerSessionIfAny();
    } finally {
      invalidateIntegratorLaborMenuQueries(queryClient);
      await queryClient.refetchQueries({ queryKey: [...LABOR_OPEN_SESSION_QUERY_KEY] });
    }
  })();
}

/**
 * Clears hub-wide labor presence (hub device session, open mobile task sessions, and the
 * per-browser dev shell lock) and notifies other tabs.
 * Used for explicit user clock-out (for example the lockdown interstitial button).
 */
export async function releaseLaborHubPresence(queryClient: QueryClient): Promise<void> {
  const row = await openSessionRowFromCacheOrFetch(queryClient);
  setLaborOwnerDeparting(true);
  try {
    optimisticallyClearOpenLaborSession(queryClient);
    clearLaborDepartureSnapshot();

    if (row !== null && row.id > 0) {
      const domain = laborDomainFromSessionPage(row.page ?? "");
      tombstoneLaborEndedSession(row.id);
      writeLaborPendingClockOut({ sessionId: row.id, domain });
      await endKnownLaborServerSessionWithRetries({ sessionId: row.id, domain, keepalive: false });
      clearLaborPendingClockOut();
      clearLaborSessionTombstone(row.id);
    }

    await endMobileLaborSession();

    clearLaborDevShellLock();
    invalidateIntegratorLaborMenuQueries(queryClient);
    await queryClient.refetchQueries({ queryKey: [...LABOR_OPEN_SESSION_QUERY_KEY] });
  } finally {
    setLaborOwnerDeparting(false);
  }
}

/**
 * Synchronous navigation clock-out prep: tombstone + optimistic clear + outbox record.
 * Server POST runs in the background via `flushLaborServerClockOut`.
 */
export function prepareLaborNavigationClockOut(queryClient: QueryClient, input: {
  sessionId: number;
  domain: LaborDomain;
}): void {
  if (input.sessionId > 0) {
    tombstoneLaborEndedSession(input.sessionId);
    writeLaborPendingClockOut({ sessionId: input.sessionId, domain: input.domain });
  }
  optimisticallyClearOpenLaborSession(queryClient);
  clearLaborDepartureSnapshot();
}
